/**
 * FloatingQuoteButton - Persistent "Get Free Quote" floating button
 * Appears on scroll, bottom-right, desktop-only (mobile uses StickyCTABar)
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquareText } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContextNew';

const labels: Record<string, string> = {
  en: 'Get Free Quote',
  fr: 'Devis Gratuit',
  es: 'Presupuesto Gratis',
  ar: 'عرض سعر مجاني',
  de: 'Kostenloses Angebot',
  it: 'Preventivo Gratuito',
  pt: 'Orçamento Grátis',
  nl: 'Gratis Offerte',
  pl: 'Darmowa Wycena',
  ru: 'Бесплатная Оценка',
  zh: '免费报价',
  ja: '無料見積もり',
  tr: 'Ücretsiz Teklif',
  ko: '무료 견적',
  sv: 'Gratis Offert',
  da: 'Gratis Tilbud',
  ro: 'Ofertă Gratuită',
  hu: 'Ingyenes Ajánlat',
  fi: 'Ilmainen Tarjous',
  hi: 'मुफ्त कोटेशन',
};

export function FloatingQuoteButton() {
  const [visible, setVisible] = useState(false);
  const { language } = useLanguage();
  const location = useLocation();

  // Hide on admin/portal/auth/checkout pages
  const hiddenPaths = ['/admin', '/portal', '/auth', '/checkout', '/agent'];
  const shouldHide = hiddenPaths.some(p => location.pathname.includes(p));

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (shouldHide) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, x: 20 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          exit={{ opacity: 0, scale: 0.8, x: 20 }}
          className="fixed bottom-24 right-6 z-40 hidden md:block"
        >
          <Link to={`/${language}/contact`}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-orange to-orange-light text-white font-semibold shadow-lg shadow-orange/30 hover:shadow-xl hover:shadow-orange/40 transition-shadow"
            >
              <MessageSquareText className="h-5 w-5" />
              {labels[language] || labels.en}
            </motion.button>
          </Link>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
